import React, { useEffect, useState } from 'react';
import { LogIn, UserPlus } from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';
import { CustomClerkSignInForm } from '../auth/CustomClerkSignInForm';
import { CustomClerkSignUpForm } from '../auth/CustomClerkSignUpForm';
import { SheetBackButton } from './SheetBackButton';

export type WebDesignedClerkAuthPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  initialMode: 'signin' | 'signup';
};

/**
 * Clerk-backed auth for the webdesigned slide-over (mobile dock / checkout “Sign in”).
 * Reuses the same custom Clerk forms and `clerkAuth.*` copy as `/login` and `/signup`.
 * Shell geometry (floating inset window) lives on the portal motion wrapper.
 */
const WebDesignedClerkAuthPanel: React.FC<WebDesignedClerkAuthPanelProps> = ({
  isOpen,
  onClose,
  initialMode,
}) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);

  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const afterAuthRedirect = '/';

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-[#ece3de]">
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-black/10 p-3">
        <SheetBackButton phone="inline" onClick={onClose} />
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex-shrink-0 rounded-lg bg-black/10 p-2">
            {mode === 'signup' ? (
              <UserPlus className="h-5 w-5 text-[#3d2817]" aria-hidden />
            ) : (
              <LogIn className="h-5 w-5 text-[#3d2817]" aria-hidden />
            )}
          </div>
          <h2 className="truncate text-base font-bold text-[#3d2817] md:text-lg">
            {mode === 'signup' ? t('clerkAuth.signUpTitle') : t('clerkAuth.signInTitle')}
          </h2>
        </div>
      </div>

      <div className="flex flex-shrink-0 border-b border-black/10 bg-[#e8ddd7]/80">
        <button
          type="button"
          onClick={() => setMode('signin')}
          className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
            mode === 'signin'
              ? 'border-b-2 border-[#3d2817] bg-[#ece3de] text-[#3d2817]'
              : 'text-[#5d4437] hover:text-[#3d2817]'
          }`}
        >
          {t('clerkAuth.signInTitle')}
        </button>
        <button
          type="button"
          onClick={() => setMode('signup')}
          className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
            mode === 'signup'
              ? 'border-b-2 border-[#3d2817] bg-[#ece3de] text-[#3d2817]'
              : 'text-[#5d4437] hover:text-[#3d2817]'
          }`}
        >
          {t('clerkAuth.signUpTitle')}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 pb-4 sm:p-4">
        {mode === 'signin' ? (
          <div className="mx-auto w-full max-w-[420px]">
            <CustomClerkSignInForm
              key="webdesigned-clerk-signin"
              signUpHref="/signup"
              afterSignInRedirect={afterAuthRedirect}
              onPressSignUp={() => setMode('signup')}
              onAfterSignInComplete={onClose}
            />
          </div>
        ) : (
          <div className="mx-auto w-full max-w-lg">
            <CustomClerkSignUpForm
              key="webdesigned-clerk-signup"
              signInHref="/signin"
              afterSignUpRedirect={afterAuthRedirect}
              onPressSignIn={() => setMode('signin')}
              onAfterSignUpComplete={onClose}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WebDesignedClerkAuthPanel;
